import { Component, EventEmitter, Input, OnInit, Output, HostListener, ElementRef, OnChanges, SimpleChanges } from '@angular/core';
import { Converter } from 'src/app/core/util/number-converter';
import { round } from 'src/app/core/util/round';
import { PaymentAccount } from '../../models/account/payment-account';
import { InvoiceLine } from '../../models/invoice/invoice-line';
import { TaxRate } from '../../models/taxRate';
import { TrackingCategory } from '../../models/tracking-categories.ts/trackingCategory';
import { QBOClass } from '../../models/qbo-class';
import { QBOCustomer } from '../../models/qbo-customer';
import {
  AccountDisplayMode,
  InvoiceDisplayFields,
  DEFAULT_INVOICE_DISPLAY_SETTINGS,
} from '../../models/invoice-display-settings';

@Component({
  selector: 'app-add-lines-one-line',
  templateUrl: './add-lines-one-line.component.html',
  styleUrls: ['./add-lines-one-line.component.scss']
})
export class AddLinesOneLineComponent implements OnInit, OnChanges {
  @Output() updateLine = new EventEmitter<InvoiceLine>();
  @Output() deleteLine = new EventEmitter<InvoiceLine>();

  @Input() accounts: PaymentAccount[] = [];
  @Input() invoiceLine: InvoiceLine;
  @Input() taxRates: TaxRate[] = [];
  @Input() selectedTaxRateId: string;
  @Input() trackingCategories: TrackingCategory[] = [];
  @Input() qboClasses: QBOClass[] = [];
  @Input() qboCustomers: QBOCustomer[] = [];
  @Input() isQuickBook: boolean = false;
  @Input() index: number = 0;
  @Input() isAddedLine: boolean = false;
  @Input() accountDisplay: AccountDisplayMode = 'nameOnly';
  // Controls which optional line fields (tracking categories, class, customer)
  // are shown, mirroring the single-line form's display settings (SD-1177).
  @Input() displayFields: InvoiceDisplayFields = { ...DEFAULT_INVOICE_DISPLAY_SETTINGS.fields };

  // Formats an account for display per the org's chosen account display mode (SD-1177).
  // Uses displayCode (Xero code / QBO AcctNum) — never `code`, which for QBO is
  // an internal id. Accounts without a display code show name only.
  accountLabel(item: PaymentAccount): string {
    if (!item) {
      return '';
    }
    if (this.accountDisplay === 'codeAndName' && item.displayCode) {
      return `${item.displayCode} - ${item.name}`;
    }
    return item.name || '';
  }

  // ng-select search predicate: always matches name; also matches the account
  // code when the org displays code + name. Arrow fn to keep `this` bound.
  accountSearchFn = (term: string, item: PaymentAccount): boolean => {
    const t = (term || '').toLowerCase();
    return (
      (item?.name || '').toLowerCase().includes(t) ||
      (this.accountDisplay === 'codeAndName' &&
        (item?.displayCode || '').toLowerCase().includes(t))
    );
  };

  @HostListener('document:click', ['$event']) clickout(event) {
    if (this.eRef.nativeElement.contains(event.target)) {
      this.isTouched = this.isAddedLine ? false : true;
    } else {
      this.isTouched = this.isAddedLine ? true : false;
      this.isAddedLine = false;
    }
  }

  selectedTrackingCategories = [null, null];
  converter = new Converter();
  showToolTipDelay: number = 400;
  isTouched: boolean = false;
  // Track raw input for math expressions
  amountRawInput: string = '';

  constructor(private eRef: ElementRef) {
  }

  ngOnInit() {
    this.updateTaxRate(this.selectedTaxRateId);
    for (let i = 0; i < this.invoiceLine.trackingOptions?.length; i++)
      this.selectedTrackingCategories[i] = this.invoiceLine.trackingOptions[i];
    // Initialize raw input with current amount
    this.amountRawInput = this.invoiceLine.subTotal?.toString() || '';
  } 

  ngOnChanges(changes: SimpleChanges) {
    // Update raw input when invoiceLine changes
    if (changes['invoiceLine'] && this.invoiceLine) {
      this.amountRawInput = this.invoiceLine.subTotal?.toString() || '';
    }
  }

  updateInvoiceLine() {
    this.updateLine.emit(this.invoiceLine);
  }

  deleteInvoiceLine() {
    this.deleteLine.emit(this.invoiceLine);
  }

  updateInvoiceLineTotals(input: any) {
    // Store the raw input so the field doesn't get cleared on partial expressions
    this.amountRawInput = input;
    let evaluated = null;
    
    if (typeof input === 'string') {
      // Remove all characters except numbers, math operators, and decimal point
      const sanitized = input.replace(/[^0-9.+\-*/()]/g, '');
      // Only try to evaluate if the input looks like a valid math expression
      if (/^[0-9().+\-*/]+$/.test(sanitized)) {
        try {
          // eslint-disable-next-line no-new-func
          evaluated = Function('return ' + sanitized)();
        } catch {
          evaluated = null;
        }
      }
    } else if (typeof input === 'number') {
      evaluated = input;
    }
    
    // If the result is a valid number, update the model
    if (typeof evaluated === 'number' && !isNaN(evaluated)) {
      if (evaluated < 0) evaluated = Math.abs(evaluated);
      this.invoiceLine.amountDue = evaluated;
      this.invoiceLine.subTotal = evaluated;
    } else {
      // If not valid, set to NaN so calculations don't break, but keep the raw input for display
      this.invoiceLine.amountDue = NaN;
      this.invoiceLine.subTotal = NaN;
    }
    
    this._updateSubTotalAndTotalTax();
    this.updateInvoiceLine();
  }

  onAmountFocusOut() {
    // Special handling for amount field: resolve math expression on blur
    let input = this.amountRawInput;
    let evaluated = null;
    if (typeof input === 'string') {
      const sanitized = input.replace(/[^0-9.+\-*/()]/g, '');
      if (/^[0-9().+\-*/]+$/.test(sanitized)) {
        try {
          evaluated = Function('return ' + sanitized)();
        } catch {
          evaluated = null;
        }
      }
    } else if (typeof input === 'number') {
      evaluated = input;
    }
    if (typeof evaluated === 'number' && !isNaN(evaluated)) {
      if (evaluated < 0) evaluated = Math.abs(evaluated);
      // Format to 2 decimals and set as both raw input and model
      this.amountRawInput = evaluated.toFixed(2);
      this.invoiceLine.amountDue = evaluated;
      this.invoiceLine.subTotal = evaluated;
      this._updateSubTotalAndTotalTax();
      this.updateInvoiceLine();
    }
    // If not valid, leave raw input as is (do not overwrite)
  }

  updateTaxRate(id: string) {
    this.selectedTaxRateId = id;
    this.invoiceLine.taxId = this.selectedTaxRateId;

    this._updateSubTotalAndTotalTax();
    this.updateInvoiceLine();
  }

  updateAccountCode(accountId: string) {
    this.invoiceLine.accountId = accountId;
    this.updateInvoiceLine();
  }

  onSelectCategory(option: string, index: number) {
    this.invoiceLine.trackingOptions[index] = option;

    this.updateInvoiceLine();
  }

  onSelectQBOClass(classId: string) {
    this.invoiceLine.classId = classId || null;
    const match = this.qboClasses?.find((c) => c.id === classId);
    this.invoiceLine.className = match?.name || null;
    this.updateInvoiceLine();
  }

  onSelectQBOCustomer(customerId: string) {
    this.invoiceLine.customerId = customerId || null;
    const match = this.qboCustomers?.find((c) => c.id === customerId);
    this.invoiceLine.customerName = match?.name || null;
    this.updateInvoiceLine();
  }

  accountSearch(term: string, item: PaymentAccount): boolean {
    return item.name.toLowerCase().includes(term);
  }

  private _updateSubTotalAndTotalTax() {
    const rate = this._getTaxRateById(this.selectedTaxRateId);

    this.invoiceLine.totalTax = round((this.invoiceLine.amountDue - (100 * this.invoiceLine.amountDue) / (100 + rate)));
    this.invoiceLine.subTotal = round((this.invoiceLine.amountDue * 100) / (100 + rate));
  }

  private _getTaxRateById(id: string): number | null {
    return this.taxRates.find(tr => tr.id === id)?.rate;
  }
}
